import { Presentation, ImageWithMetadata } from '@features/powerpoint';
import { Campaign } from '@features/campaigns';
import {
  SALES_ATTRIBUTION_TITLE,
  VISIT_ATTRIBUTION_TITLE,
} from '@features/executive-report/domain/slide-titles';
import { DEFAULT_FILENAME } from '@features/executive-report/utils';
import { createSharedPresentationConfig } from '@features/executive-report/utils/shared-presentation-config';
import { FormSchema } from '../form-schema';
import {
  visibleSlide,
  meetYourTeamSlide,
  agendaSlide,
  navigatingComplexitySlide,
  incrementalityMattersSlide,
  coverSlide,
  titleSlide,
  recommendationsSlide,
} from '@features/executive-report/slides';
import { discoverWhatsNextSlide } from '@features/executive-report/slides/discover-whats-next';
import { RslReport } from '@features/campaigns/subdomains/rsl';
import {
  rslCampaignOverviewSlide,
  salesDimensionsSlides,
  salesOptimizationSummarySlide,
} from '../slides';
import { campaignOverviewSlide2 } from '../slides/campaign-overview-2';
import {
  visitOverviewSlide,
  visitDimensionsSlides,
} from '../../visitation/slides';
import { VisitMetrics } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { SubdimensionsByName } from '@features/reports/subdomains/visitation/domain/dimensions';
import { ValueInput } from '@utils/formattersV2';
import { countSignificantDimensions } from '../domain/significant-dimensions';
import { RSL_DIMENSION_SLUGS } from '../constants/dimensions';

export type BuildReportOptions = {
  brandLogo: ImageWithMetadata | null;
  campaign: Pick<Campaign, 'name' | 'startDate' | 'endDate' | 'adSpend'>;
  customFields: FormSchema;
  report: RslReport;
  midCampaignReport: RslReport | null;
  iroas: number | null;
  visitMetrics: VisitMetrics;
  topVisitSubdimensionsByName: SubdimensionsByName;
  costPerIncrementalVisit: ValueInput;
};

export const buildPresentation = (
  options: BuildReportOptions
): Presentation => {
  const basePresentation = createSharedPresentationConfig(options.brandLogo);
  const hasVisitMetrics = options.report.hasVisitMetrics;
  const [audSlug, creativeSlug, channelSlug] = RSL_DIMENSION_SLUGS;

  return {
    ...basePresentation,
    filename: [options.campaign.name, DEFAULT_FILENAME].join(' - '),
    slides: [
      ...visibleSlide({
        slide: titleSlide(
          options.campaign,
          options.brandLogo,
          options.customFields.campaignName
        ),
        visible: options.customFields.includedSlides.intro.title,
      }),
      ...visibleSlide({
        slide: meetYourTeamSlide({
          teamMembers: options.customFields.teamMembers,
        }),
        visible: options.customFields.includedSlides.intro.meetYourTeam,
      }),
      ...visibleSlide({
        slide: agendaSlide([
          { title: 'The Who', text: ' – Audience' },
          { title: 'The What', text: ' – Creative' },
          { title: 'The Where', text: ' – Channels' },
        ]),
        visible: options.customFields.includedSlides.intro.agenda,
      }),
      ...visibleSlide({
        slide: navigatingComplexitySlide(),
        visible: options.customFields.includedSlides.intro.navigatingComplexity,
      }),
      ...visibleSlide({
        slide: incrementalityMattersSlide({
          liftText: hasVisitMetrics
            ? 'lift in visits and transaction level sales'
            : 'lift in transaction level sales',
        }),
        visible:
          options.customFields.includedSlides.intro.incrementalityMatters,
      }),
      ...visibleSlide({
        slide: coverSlide({ titleText: SALES_ATTRIBUTION_TITLE }),
        visible: options.customFields.includedSlides.sales.cover,
      }),
      ...visibleSlide({
        slide: rslCampaignOverviewSlide({
          brandLogoWithMetadata: options.brandLogo,
          campaign: {
            startDate: options.campaign.startDate,
            endDate: options.campaign.endDate,
            adSpend: options.campaign.adSpend ?? 0,
          },
          report: options.report,
          dimensionCounts: [
            countSignificantDimensions(options.report.dimensions, audSlug),
            countSignificantDimensions(options.report.dimensions, creativeSlug),
            countSignificantDimensions(options.report.dimensions, channelSlug),
          ],
        }),
        visible: options.customFields.includedSlides.sales.campaignOverview,
      }),
      ...visibleSlide({
        slide: campaignOverviewSlide2({
          brandLogoWithMetadata: options.brandLogo,
          report: options.report,
          iroas: options.iroas ?? 0,
          objectives: options.customFields.salesObjectives.map(o => o.value),
        }),
        visible: options.customFields.includedSlides.sales.campaignOverview2,
      }),
      ...salesDimensionsSlides({
        report: options.report,
        recommendations: options.customFields.salesDimensionRecommendations,
        includedSlides: options.customFields.includedSlides.sales,
        subdimensionImages:
          options.customFields.subdimensionImagesByNameAndValue,
      }),
      ...visibleSlide({
        slide: salesOptimizationSummarySlide({
          midCampaignReport: options.midCampaignReport,
          currentReport: options.report,
        }),
        visible: options.customFields.includedSlides.sales.optimizationSummary,
      }),
      ...(hasVisitMetrics
        ? [
            ...visibleSlide({
              slide: coverSlide({ titleText: VISIT_ATTRIBUTION_TITLE }),
              visible: options.customFields.includedSlides.footTraffic.cover,
            }),
            ...visibleSlide({
              slide: visitOverviewSlide({
                brandLogo: options.brandLogo,
                objectives: options.customFields.footTrafficObjectives.map(
                  o => o.value
                ),
                visitMetrics: options.visitMetrics,
                costPerIncrementalVisit: options.costPerIncrementalVisit,
              }),
              visible: options.customFields.includedSlides.footTraffic.overview,
            }),
            ...visitDimensionsSlides({
              visitMetrics: options.visitMetrics,
              subdimensionsByName: options.topVisitSubdimensionsByName,
              recommendations:
                options.customFields.footTrafficDimensionRecommendations,
              includedSlides: options.customFields.includedSlides.footTraffic,
              subdimensionImages:
                options.customFields.subdimensionImagesByNameAndValue,
            }),
          ]
        : []),
      ...visibleSlide({
        slide: recommendationsSlide(
          options.customFields.recommendations.map(r => r.value),
          []
        ),
        visible: options.customFields.includedSlides.takeaways.recommendations,
      }),
      ...visibleSlide({
        slide: discoverWhatsNextSlide({
          observations: options.customFields.observations.map(o => o.value),
        }),
        visible:
          options.customFields.includedSlides.takeaways.discoverWhatsNext,
      }),
    ],
  };
};
